//  ----------      START   IMPORT  BLOCK       ----------
import React, { useState, useEffect } from "react";
//  Widgets
import { Card, Form, Button, Row, Col, Spinner, Alert } from "react-bootstrap";
//  Context
import { useLanguage } from "../../../../API/Context/LanguageContext";
//  Hooks & Type-Only Imports
import { useUserContact, type ContactFields } from "../../../../API/Hooks/useUserContact"; 
import type { EmergencyContact } from "../../../../Types/schema-helpers";
import EnumSelect from "../ProfileOnboarding/Steps/Widgets/EnumSelect";
import { RELATIONSHIPS } from "../../../../ConfigFiles/maps/relationships_map";
import { RelationshipsEnum } from "../../../../Types/Enums/RelationshipsEnum";
//  Config
import { VocabButtons as btn_vocab } from "../../../../ConfigFiles/multilingual/VocabButtons";
import { VocabContactWidget } from "../../../../ConfigFiles/multilingual/VocabContactWidget";
//  ----------      END   IMPORT  BLOCK       ----------

const INITIAL_LOCAL_DATA: ContactFields = {
  phone_numbers: [],
  emergency_contacts: [],
};

const ContactInfoWidget: React.FC = () => {
  const { lan } = useLanguage();
  const { userContact, isLoading, apiError, updateContact } = useUserContact(); 

  const [localData, setLocalData] = useState<ContactFields>(INITIAL_LOCAL_DATA);
  const [isSaving, setIsSaving] = useState<boolean>(false); 
  const [localError, setLocalError] = useState<string | null>(null); 

  // Synchronize local state only when contextual userContact changes
  useEffect(() => {
    if (userContact) {
      setLocalData({
        phone_numbers: userContact.phone_numbers ?? [],
        emergency_contacts: userContact.emergency_contacts ?? [],
      });
    }
  }, [userContact]);

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    setLocalError(null);
    
    const success = await updateContact(localData);
    
    setIsSaving(false);

    if (!success && !apiError) {
      setLocalError(VocabContactWidget.error_saving?.[lan] || "Failed to save changes.");
    }
  };

  const handlePhoneChange = (index: number, value: string): void => {
    setLocalData((prev) => {
      const updatedPhones = [...(prev.phone_numbers ?? [])];
      if (index < 0 || index >= updatedPhones.length) return prev;
      
      updatedPhones[index] = value;
      return { ...prev, phone_numbers: updatedPhones };
    });
  };

  const addPhone = (): void => {
    setLocalData((prev) => ({
      ...prev,
      phone_numbers: [...(prev.phone_numbers ?? []), ""],
    }));
  };

  const removePhone = (index: number): void => {
    setLocalData((prev) => {
      const updatedPhones = [...(prev.phone_numbers ?? [])];
      if (index < 0 || index >= updatedPhones.length) return prev;

      updatedPhones.splice(index, 1);
      return { ...prev, phone_numbers: updatedPhones };
    });
  };

  const handleContactChange = <K extends keyof EmergencyContact>(
    index: number,
    field: K,
    value: EmergencyContact[K]
  ): void => {
    setLocalData((prev) => {
      const contacts = prev.emergency_contacts ?? [];
      const target = contacts[index];
      if (!target) return prev;

      const updatedContacts = [...contacts];
      updatedContacts[index] = { ...target, [field]: value };
      
      return { ...prev, emergency_contacts: updatedContacts };
    });
  };

  const addContact = (): void => {
    const defaultRelationship = (RELATIONSHIPS[0]?.id ?? RelationshipsEnum.PARENT) as EmergencyContact["relationship"];

    setLocalData((prev) => ({
      ...prev,
      emergency_contacts: [
        ...(prev.emergency_contacts ?? []),
        { 
          name: "", 
          phone: "", 
          relationship: defaultRelationship 
        }, 
      ],
    }));
  };

  const removeContact = (index: number): void => {
    setLocalData((prev) => {
      const updatedContacts = [...(prev.emergency_contacts ?? [])];
      if (index < 0 || index >= updatedContacts.length) return prev;

      updatedContacts.splice(index, 1);
      return { ...prev, emergency_contacts: updatedContacts };
    });
  };

  const displayError = localError || apiError;
  const showInitialLoading = isLoading && !userContact;

  if (showInitialLoading) {
    return (
      <Card className="mb-3 p-5 text-center shadow-lg rounded-xl">
        <Spinner animation="border" role="status" className="mx-auto" />
        <p className="mt-2">{VocabContactWidget.loading_data?.[lan] || "Loading..."}</p>
      </Card>
    );
  }

  return (
    <Card className="mb-3 shadow-lg rounded-xl">
      <Card.Header className="d-flex justify-content-between align-items-center bg-primary text-white rounded-t-xl p-3">
        <Card.Title className="mb-0 text-xl font-bold">
          {VocabContactWidget.contact_information?.[lan] || "Contact Information"}
        </Card.Title>
        <Button 
          variant="light" 
          size="sm" 
          onClick={handleSave} 
          disabled={isSaving || isLoading}
        >
          {isSaving ? (
            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
          ) : (
            btn_vocab.save_changes?.[lan] || "Save Changes"
          )}
        </Button>
      </Card.Header>

      <Card.Body className="p-4">
        {displayError && <Alert variant="danger">{displayError}</Alert>}
        
        {/* Phone Numbers Section */}
        <h6 className="text-lg font-semibold mb-3 border-b pb-1 text-primary">
          {VocabContactWidget.phone_numbers?.[lan] || "Phone Numbers"}
        </h6>
        {(localData.phone_numbers ?? []).map((phone, index) => (
          <Row key={index} className="mb-3 align-items-center">
            <Col xs={9}>
              <Form.Control
                placeholder={VocabContactWidget.phone_number?.[lan] || "Phone Number"}
                type="text"
                value={phone}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                  handlePhoneChange(index, e.target.value)
                }
              />
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removePhone(index)}
                aria-label={btn_vocab.remove?.[lan] || "Remove"}
              >
                &times;
              </Button>
            </Col>
          </Row>
        ))}
        <Button variant="outline-secondary" size="sm" onClick={addPhone} className="mt-2">
          {btn_vocab.add?.[lan] || "Add"}
        </Button>

        {/* Emergency Contacts Section */}
        <h6 className="mt-5 text-lg font-semibold mb-3 border-b pb-1 text-primary">
          {VocabContactWidget.emergency_contacts?.[lan] || "Emergency Contacts"}
        </h6>
        {(localData.emergency_contacts ?? []).map((contact, index) => (
          <Row key={index} className="mb-3 align-items-center">
            <Col md={4} sm={12} className="mb-2 mb-md-0">
              <Form.Control
                placeholder={VocabContactWidget.name_placeholder?.[lan] || "Name"}
                value={contact.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                  handleContactChange(index, "name", e.target.value)
                }
              />
            </Col>
            <Col md={4} sm={12} className="mb-2 mb-md-0">
              <Form.Control
                placeholder={VocabContactWidget.phone_number?.[lan] || "Phone"}
                value={contact.phone}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                  handleContactChange(index, "phone", e.target.value)
                }
              />
            </Col>
            <Col md={3} sm={12} className="mb-2 mb-md-0">
              <EnumSelect 
                value={contact.relationship}
                options={RELATIONSHIPS}
                onChange={(val) => handleContactChange(index, "relationship", val)}
              />
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeContact(index)}
                aria-label={btn_vocab.remove?.[lan] || "Remove"}
              >
                &times;
              </Button>
            </Col>
          </Row>
        ))}
        <Button variant="outline-secondary" size="sm" onClick={addContact} className="mt-2">
          {btn_vocab.add?.[lan] || "Add"}
        </Button>
      </Card.Body>
    </Card>
  );
};

export default ContactInfoWidget;